<script lang="ts">
  import {
    ref,
    type Ref,
    type ComputedRef,
    computed,
    watch,
    nextTick
  } from 'vue'
  import {
    type SearchContextType,
    type SearchResult,
    stakeholdersSearchRequest,
    SearchFilterProvider,
    type SearchFieldModelValue
  } from '@gat-ship/search'
  import { createContext } from 'reka-ui'
  import {
    type CurrentSearch,
    buildStakeholderFilterQuery,
    useStakeholderSearchFilters,
    getCurrentSearchFromFilters,
    type StakeholderSearchFilterActiveState,
    type StakeholderSearchFilterKeys
  } from './utils-search'
  import type {
    Stakeholder,
    StakeholderType,
    GatshipSearchOperator
  } from '@gat-ship/types'
  import { icons, type IconValues } from '@gat-ship/icons'
  import { useDebounceFn } from '@vueuse/core'

  export type StakeholderSearchFilterType = 'client' | 'vendor' | 'other'

  export interface StakeholderSearchFilterTypeMetadata {
    title: string
    subtitle: string
    icon: IconValues
  }
  export const stakeholderSearchFilterTypeMetadata: Record<
    StakeholderSearchFilterType,
    StakeholderSearchFilterTypeMetadata
  > = {
    client: {
      title: 'Client',
      subtitle: 'Show only clients',
      icon: icons.stakeholders.client.base
    },
    vendor: {
      title: 'Vendor',
      subtitle: 'Show only vendors',
      icon: icons.stakeholders.vendor.base
    },
    other: {
      title: 'Other',
      subtitle: 'Show only other parties',
      icon: icons.stakeholders.other.base
    }
  }

  export type StakeholderSearchMode = 'advanced' | 'simple'

  export type StakeholderSearchFilterStatus = 'active' | 'inactive' | 'any'

  export interface StakeholderSearchFilterStatusMetadata {
    title: string
    subtitle: string
    icon: IconValues
  }

  export const statusMetadata: Record<
    StakeholderSearchFilterStatus,
    StakeholderSearchFilterStatusMetadata
  > = {
    active: {
      title: 'Active',
      subtitle: 'Show only active stakeholders',
      icon: 'CircleDot'
    },
    inactive: {
      title: 'Archived',
      subtitle: 'Show only archived stakeholders',
      icon: 'Archive'
    },
    any: {
      title: 'Both',
      subtitle: 'Show both active and archived stakeholders',
      icon: 'Layers'
    }
  }

  export type AdvancedSearchStatus = 'editing' | 'searching' | 'finished'

  function useSearchState() {
    const searchStatus = ref<AdvancedSearchStatus>('editing')

    function setSearchStatus(status: AdvancedSearchStatus) {
      searchStatus.value = status
    }

    const currentSearch = ref<CurrentSearch | undefined>(undefined)
    function setCurrentSearch(search: CurrentSearch | undefined) {
      currentSearch.value = search
    }
    const searchResults = ref<SearchResult<Stakeholder>[] | undefined>(
      undefined
    )
    function setSearchResults(
      results: SearchResult<Stakeholder>[] | undefined
    ) {
      searchResults.value = results
    }

    function clearSearchResults() {
      searchResults.value = undefined
    }

    return {
      searchStatus,
      setSearchStatus,
      currentSearch,
      setCurrentSearch,
      searchResults,
      setSearchResults,
      clearSearchResults
    }
  }

  function useQuickSearchState() {
    const activeAbortController = ref<AbortController | undefined>(undefined)
    const pendingSearchValue = ref<string | undefined | null>(undefined)

    function abortActiveSearch() {
      if (activeAbortController.value) {
        activeAbortController.value.abort()
        activeAbortController.value = undefined
      }
    }

    const searchState = useSearchState()

    const searchInputValue = ref<string | undefined>(undefined)

    async function callSearch(
      inputValue: string | undefined | null,
      signal?: AbortSignal
    ) {
      if (!inputValue?.trim()) {
        // searchState.clearSearchResults()
        searchState.setSearchStatus('editing')
        return
      }
      if (
        searchState.searchResults.value &&
        searchState.searchResults.value === undefined
      ) {
        searchState.setSearchStatus('searching')
      }
      searchState.setCurrentSearch({
        searchTerm: inputValue,
        activeFilters: {}
      })

      try {
        const response = await stakeholdersSearchRequest(
          {
            query: `${inputValue}`,
            queryMode: 'free-text'
          },
          {
            signal
          }
        )
        searchState.setSearchResults(response)
        searchState.setSearchStatus('finished')
      } catch (error) {
        if ((error as any)?.code === 'ERR_CANCELED') {
          return
        }
        searchState.setSearchStatus('finished')
        throw error
      }
    }

    const debouncedCallSearch = useDebounceFn(
      (value: string | undefined | null) => {
        // Check if the search value has changed since this was scheduled
        // If it has, don't execute (a new debounced call will handle it)
        if (pendingSearchValue.value !== value) {
          return
        }
        const abortController = new AbortController()
        activeAbortController.value = abortController
        callSearch(value, abortController.signal)
      },
      500
    )

    watch(searchInputValue, (value) => {
      abortActiveSearch()
      // Cancel any pending debounced call
      if (
        'cancel' in debouncedCallSearch &&
        typeof debouncedCallSearch.cancel === 'function'
      ) {
        debouncedCallSearch.cancel()
      }
      // Update the pending search value
      pendingSearchValue.value = value
      if (value?.trim()) {
        searchState.setSearchStatus('searching')
        debouncedCallSearch(value)
      } else {
        searchState.clearSearchResults()
        searchState.setSearchStatus('editing')
      }
    })

    return {
      ...searchState,
      callSearch,
      searchInputValue
    }
  }

  function useAdvancedSearchState() {
    const searchFilters = useStakeholderSearchFilters()

    function setSearchFilters(filters: CurrentSearch['activeFilters']) {
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          searchFilters[key as StakeholderSearchFilterKeys].filterValue.value =
            value.filterValue
          searchFilters[key as StakeholderSearchFilterKeys].operator.value =
            value.operator
          searchFilters[key as StakeholderSearchFilterKeys].fieldName =
            value.fieldName
        })
      }
    }

    function resetSearchFilters() {
      Object.values(searchFilters).forEach((filter) => {
        if (filter.fieldName === 'type') {
          filter.filterValue.value = 'client' satisfies StakeholderType
          return
        }
        if (filter.fieldName === 'state') {
          filter.filterValue.value =
            'active' satisfies StakeholderSearchFilterActiveState
          return
        }
        filter.filterValue.value = undefined
      })
    }

    const searchState = useSearchState()

    const currentSearch = ref<CurrentSearch | undefined>(undefined)

    function setCurrentSearch(search: CurrentSearch | undefined) {
      currentSearch.value = search
    }

    function editCurrentSearch() {
      searchState.setSearchStatus('editing')
    }

    function clearCurrentSearch() {
      searchState.setSearchStatus('editing')
      searchState.setCurrentSearch(undefined)
      resetSearchFilters()
    }

    async function callFiltersSearch() {
      searchState.setSearchStatus('searching')
      const query = buildStakeholderFilterQuery(searchFilters)
      const currentSearch = getCurrentSearchFromFilters(searchFilters)
      currentSearch.searchTerm = query
      setCurrentSearch(currentSearch)
      const response = await stakeholdersSearchRequest({
        query,
        queryMode: 'fields'
      })
      searchState.setSearchResults(response)
      searchState.setSearchStatus('finished')
    }

    function isFiltersDirty() {
      const excluded: StakeholderSearchFilterKeys[] = ['state', 'type']
      return Object.values(searchFilters)
        .filter(
          (filter) =>
            !excluded.includes(filter.fieldName as StakeholderSearchFilterKeys)
        )
        .some(
          (filter) =>
            filter.filterValue.value !== undefined &&
            filter.filterValue.value !== ''
        )
    }

    function isSearchPossible() {
      return isFiltersDirty()
    }

    function clearSearch() {
      clearCurrentSearch()
      searchState.clearSearchResults()
      searchState.setSearchStatus('editing')
    }

    return {
      ...searchState,
      searchFilters,
      setSearchFilters,
      currentSearch,
      setCurrentSearch,
      clearCurrentSearch,
      editCurrentSearch,
      callFiltersSearch,
      isFiltersDirty,
      isSearchPossible,
      clearSearch
    }
  }

  function useStakeholdersOverviewPageState() {
    /** Simple way to keep track of the current tab to navigate to the correct route when the user clicks the search button in the main navigation */
    const currentSearchTab = ref<'advanced' | 'quick'>('quick')
    return {
      currentSearchTab
    }
  }

  export interface StakeholderSearchContext {
    quickSearchState: ReturnType<typeof useQuickSearchState>
    advancedSearchState: ReturnType<typeof useAdvancedSearchState>
    pageState: ReturnType<typeof useStakeholdersOverviewPageState>
  }

  export const [useStakeholderSearch, provideStakeholderSearch] =
    createContext<StakeholderSearchContext>('gs-stakeholders-search')
</script>
<script setup lang="ts">
  const advancedSearchState = useAdvancedSearchState()

  const quickSearchState = useQuickSearchState()

  const pageState = useStakeholdersOverviewPageState()

  provideStakeholderSearch({
    quickSearchState,
    advancedSearchState,
    pageState
  })
</script>

<template>
  <search-filter-provider :search-filters="advancedSearchState.searchFilters">
    <slot />
  </search-filter-provider>
</template>
